import React, { useState } from "react";

import { TopMenu } from "../top-menu";
import { useMessageToState } from "../../../hooks/useMessageToState";
import { MenuIds, useMenus } from "../../../hooks/useMenus";

import { useTextZoneDashboard } from "../../../hooks/useTextZoneDashboard";
import { useStableCallback } from "../../../hooks/useStableCallback";
import { useTextStyleButtons } from "../../bottom-text-menus/hooks/use-text-style-buttons";
import { useTextDeleteClearButtons } from "../../bottom-text-menus/hooks/use-text-delete-clear-buttons";
import { useCopyPasteZone } from "../../../hooks/useCopyPasteZone";
import { useArrowKeyZoneMove } from "../../../hooks/useArrowKeyZoneMove";
import { useKeyDown } from "../../../hooks/useKeyboard";
import { useLogicalSelectedPage, useSelectedZone } from "../../../hooks/useSelection";
import { TextSizeNumberField } from "../top-menu-numberfield/text-size-numberfield";
import { TextAlignmentButton } from "../text-alignment-button/text-alignment-button";
import { useIsProductAdvancedEditMode } from "../../../hooks/useIsProductAdvancedEditMode";
import { ZoneDepthDropdown } from "../../zone-depth-dropdown/zone-depth-dropdown";
import { Tooltip } from "../../tooltip/tooltip";
import { labels } from "./labels";
import { useLabels } from "../../../hooks/useLabels";
import { useSelectedTextColor } from "../../bottom-text-menus/text-color-panel/use-selected-text-color";
import { TextColorIcon } from "../../../icons/icons";

import "./text-top-menu.css";

const useTopTextMenu = () => {
	const textDashboard = useTextZoneDashboard();
	const isAdvancedEditModeEnabled = useIsProductAdvancedEditMode();

	const getTopTextMenuState = useStableCallback((editor: eXpress.core.IEditor) => {
		const zone = editor.selection.getCurrentZone();
		const textZone = eXpress.core.isAnyTextZone(zone) ? zone : undefined;

		return {
			hide: !textZone || (!zone?.canChangeContent() && !zone?.canChangeDesign()),
			fontFamily: textZone?.textSettings.fontFamily,

			canSetFontFamily: textDashboard.canSetFontFamily(zone),
			canSetFontSize: textDashboard.canSetFontSize(zone),
			canSetTextColor: textDashboard.canSetTextColor(zone),
			showTextStyling:
				textDashboard.isTextBoldSupported(zone) ||
				textDashboard.isTextItalicSupported(zone) ||
				textDashboard.isTextUnderlineSupported(zone),
			canSetHorizontalAlignment: textDashboard.canSetHorizontalAlignment(zone),
			canSetVerticalAlignment: textDashboard.canSetVerticalAlignment(zone),
			canChangeZoneDepth: isAdvancedEditModeEnabled,
		};
	});

	// After any message, update the state
	return useMessageToState({
		name: "useTopTextMenu",
		getState: getTopTextMenuState,
	});
};

export function TextTopMenu() {
	const selectedZone = useSelectedZone();
	const selectedPage = useLogicalSelectedPage();
	const textZone = eXpress.core.isAnyTextZone(selectedZone) ? selectedZone : undefined;

	const {
		hide,
		fontFamily,
		canSetFontFamily,
		canSetFontSize,
		canSetTextColor,
		showTextStyling,
		canSetHorizontalAlignment,
		canSetVerticalAlignment,
		canChangeZoneDepth,
	} = useTopTextMenu();

	const zone = useSelectedZone();
	const textColor = useSelectedTextColor(zone);
	const { bold, italic, underline } = useTextStyleButtons();

	const { clear, delete: del } = useTextDeleteClearButtons();

	const {
		menuId,
		actions: { setMenu },
	} = useMenus();

	// Register Ctrl+C/V for the selected text zone.
	useCopyPasteZone({
		zone: textZone,
		destinationPage: selectedPage ?? undefined,
	});

	useArrowKeyZoneMove({ zone: textZone });

	// Used a separate state, so we don't change opened panel in the sidebar
	const [openDepthDropDown, setOpenDepthDropDown] = useState(menuId === MenuIds.TextZoneDepth);
	const [openOverflowDepthDropDown, setOpenOverflowDepthDropDown] = useState(
		menuId === MenuIds.TextZoneDepth,
	);
	const [overflowOpen, setOverflowOpen] = useState(false);

	const [[fontText, fontColorText, boldText, italicText, underlineText, clearText, deleteText]] =
		useLabels([
			labels.fontTooltip,
			labels.fontColorTooltip,
			labels.boldTooltip,
			labels.italicTooltip,
			labels.underlineTooltip,
			labels.clearTooltip,
			labels.deleteTooltip,
		]);

	// Support Delete press
	useKeyDown((event) => {
		if (event.key === "Delete") {
			if (!clear.isDisabled) {
				if (clear && !clear.isDisabled) {
					clear.onClick(event);
				}
			} else {
				if (del && !del.isDisabled && del.isSupported) {
					del.onClick(event);
				}
			}
		}
	});

	if (hide) return null;

	return (
		<TopMenu.Root>
			<TopMenu.Bar>
				{
					/* Font Families */
					canSetFontFamily && (
						<>
							<TopMenu.Group>
								<Tooltip text={fontText}>
									<TopMenu.Button
										className="text-top-menu__font-button"
										checked={menuId === MenuIds.FontFamilies}
										outlined
										onClick={() => {
											setMenu(
												menuId === MenuIds.FontFamilies
													? MenuIds.Text
													: MenuIds.FontFamilies,
											);
											// Ideally we move focus to the opened menu
										}}
									>
										{fontFamily?.name ?? ""}
									</TopMenu.Button>
								</Tooltip>
							</TopMenu.Group>
						</>
					)
				}

				{canSetFontSize && <TextSizeNumberField />}

				{canSetTextColor && (
					<Tooltip text={fontColorText}>
						<TopMenu.Icon
							checked={menuId === MenuIds.TextColor}
							onClick={() => {
								setMenu(
									menuId === MenuIds.TextColor ? MenuIds.Text : MenuIds.TextColor,
								);
								// Ideally we move focus to the opened menu
							}}
						>
							<TextColorIcon
								textColor={textColor}
								style={{ transform: "translateY(1px)" }}
							/>
						</TopMenu.Icon>
					</Tooltip>
				)}

				{showTextStyling && (
					<>
						<TopMenu.Group hideLevel={7}>
							<Tooltip text={boldText}>
								<TopMenu.Icon
									iconName={bold.iconName}
									disabled={bold.isDisabled}
									checked={bold.isActive}
									onClick={bold.onClick}
								/>
							</Tooltip>
						</TopMenu.Group>

						<TopMenu.Group hideLevel={6}>
							<Tooltip text={italicText}>
								<TopMenu.Icon
									iconName={italic.iconName}
									disabled={italic.isDisabled}
									checked={italic.isActive}
									onClick={italic.onClick}
								/>
							</Tooltip>
						</TopMenu.Group>

						<TopMenu.Group hideLevel={5}>
							<Tooltip text={underlineText}>
								<TopMenu.Icon
									iconName={underline.iconName}
									disabled={underline.isDisabled}
									checked={underline.isActive}
									onClick={underline.onClick}
								/>
							</Tooltip>
						</TopMenu.Group>
					</>
				)}

				{canSetHorizontalAlignment && (
					<TopMenu.Group hideLevel={4}>
						<TextAlignmentButton direction="horizontal" />
					</TopMenu.Group>
				)}

				{canSetVerticalAlignment && (
					<TopMenu.Group hideLevel={3}>
						<TextAlignmentButton direction="vertical" />
					</TopMenu.Group>
				)}

				{canChangeZoneDepth && (
					<TopMenu.Group
						hideLevel={2}
						onVisibilityChange={(isVisible) => {
							if (!isVisible) {
								setOpenDepthDropDown(false);
							}
						}}
					>
						<ZoneDepthDropdown
							open={openDepthDropDown}
							onOpenChange={setOpenDepthDropDown}
						/>
					</TopMenu.Group>
				)}

				<TopMenu.Group>
					{del.showDelete ? (
						<Tooltip text={deleteText}>
							<TopMenu.Icon
								iconName={del.iconName}
								disabled={del.isDisabled}
								onClick={del.onClick}
							/>
						</Tooltip>
					) : (
						<Tooltip text={clearText}>
							<TopMenu.Icon
								iconName={clear.iconName}
								disabled={clear.isDisabled}
								onClick={clear.onClick}
							/>
						</Tooltip>
					)}
				</TopMenu.Group>
				<TopMenu.Group showLevel={1}>
					<TopMenu.Icon
						iconName="dots-horizontal"
						onClick={() => setOverflowOpen((o) => !o)}
					/>
				</TopMenu.Group>
			</TopMenu.Bar>
			<TopMenu.Group showLevel={1} style={{ position: "relative" }}>
				<TopMenu.OverflowBar show={overflowOpen}>
					{showTextStyling && (
						<>
							<TopMenu.Group showLevel={7}>
								<Tooltip text={boldText}>
									<TopMenu.Icon
										iconName={bold.iconName}
										disabled={bold.isDisabled}
										checked={bold.isActive}
										onClick={bold.onClick}
									/>
								</Tooltip>
							</TopMenu.Group>
							<TopMenu.Group showLevel={6}>
								<Tooltip text={italicText}>
									<TopMenu.Icon
										iconName={italic.iconName}
										disabled={italic.isDisabled}
										checked={italic.isActive}
										onClick={italic.onClick}
									/>
								</Tooltip>
							</TopMenu.Group>
							<TopMenu.Group showLevel={5}>
								<Tooltip text={underlineText}>
									<TopMenu.Icon
										iconName={underline.iconName}
										disabled={underline.isDisabled}
										checked={underline.isActive}
										onClick={underline.onClick}
									/>
								</Tooltip>
							</TopMenu.Group>
						</>
					)}
					<TopMenu.Group showLevel={4}>
						<TextAlignmentButton direction="horizontal" />
					</TopMenu.Group>
					<TopMenu.Group showLevel={3}>
						<TextAlignmentButton direction="vertical" />
					</TopMenu.Group>
					<TopMenu.Group
						showLevel={2}
						onVisibilityChange={(isVisible) => {
							if (!isVisible) {
								setOpenOverflowDepthDropDown(false);
							}
						}}
					>
						<ZoneDepthDropdown
							open={openOverflowDepthDropDown}
							onOpenChange={setOpenOverflowDepthDropDown}
						/>
					</TopMenu.Group>
				</TopMenu.OverflowBar>
			</TopMenu.Group>
		</TopMenu.Root>
	);
}
